import { ReactNode, useMemo, useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import Modal from 'components/shared/Modal';
import Item from 'components/shared/settings/Item';
import Input from 'components/shared/Input';
import Button from 'components/shared/Button';
import { SettingsButtons } from 'components/shared/Settings';
import settingsStyles from 'theme/react-select/settings';
import {
  useCreateCompetenceMutation,
  useDeleteCompetenceMutation,
  useUpdateCompetenceMutation,
} from 'utils/wemble-api';
import { CategoryNode, pathOf, subtreeCategoryIds, subtreeLeaves } from 'components/shared/UserCard/competenceTree';

// Same look as the app's other settings modals (CompetenceSettings).
const useStyles = makeStyles((theme) => ({
  title: {
    fontSize: 24,
    fontWeight: 600,
    padding: '0 0 16px',
    margin: 0,
  },
  text: {
    fontSize: 15,
    color: theme.palette.gray.g1,
    margin: '0 0 8px',
    lineHeight: 1.5,
  },
  error: {
    color: theme.palette.red.r4,
    fontSize: 14,
    margin: '8px 0 0',
  },
  buttons: {
    marginTop: 32,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  delete: {
    backgroundColor: theme.palette.red.opacity.r16,
    color: theme.palette.red.r4,
    padding: '10px 18px',
    fontWeight: 500,
    borderRadius: 8,
    cursor: 'pointer',
    userSelect: 'none',
    display: 'inline-block',
  },
}));

const errorMessage = (error: unknown) =>
  (error as { data?: { message?: string } })?.data?.message || 'Something went wrong. Please try again.';

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

type Option = { value: string; label: string };

/** Categories go two levels deep: a category and its subcategories.
 *  (Older data can be deeper; it keeps working, but nothing new goes deeper.) */
export const MAX_CATEGORY_DEPTH = 2;

/** One optional dropdown of categories, labelled with their full path
 *  ("Rettsområder › Bank og finans"). Clearing it means none. `topLevelOnly`
 *  limits it to top-level categories, for picking a category's parent; the
 *  current value always stays listed so legacy deeper data still displays. */
const CategorySelect = ({
  tops,
  value,
  onChange,
  exclude,
  topLevelOnly = false,
  placeholder,
}: {
  tops: CategoryNode[];
  value: string | null;
  onChange: (value: string | null) => void;
  exclude?: Set<string>;
  topLevelOnly?: boolean;
  placeholder: string;
}) => {
  const options = useMemo(() => {
    const out: Option[] = [];
    const walk = (node: CategoryNode) => {
      if (exclude?.has(node.id)) return; // a category can't go inside itself
      const depth = pathOf(node).length;
      if (!topLevelOnly || depth === 1 || node.id === value) {
        out.push({
          value: node.id,
          label: pathOf(node)
            .map((n) => n.name)
            .join(' › '),
        });
      }
      node.children.forEach(walk);
    };
    tops.forEach(walk);
    return out;
  }, [tops, exclude, topLevelOnly, value]);

  return (
    <div style={{ width: '100%', display: 'inline-block' }}>
      <Select
        className="basic-single"
        classNamePrefix="select"
        isClearable
        placeholder={placeholder}
        options={options}
        value={options.find((o) => o.value === value) ?? null}
        onChange={(option) => onChange((option as Option | null)?.value ?? null)}
        styles={settingsStyles}
      />
    </div>
  );
};

/** Add a category or a competency. A new category starts with no parent (top
 *  level; pick one to make it a subcategory); a new competency starts in the
 *  category being viewed. Either can be cleared. */
export const AddDialog = ({
  kind,
  parent,
  tops,
  onClose,
  onCreated,
}: {
  kind: 'category' | 'competence';
  parent: CategoryNode | null;
  tops: CategoryNode[];
  onClose: () => void;
  onCreated?: (id: string, kind: 'category' | 'competence') => void;
}) => {
  const classes = useStyles();
  const [createCompetence] = useCreateCompetenceMutation();
  const [error, setError] = useState<string | null>(null);
  const isCategory = kind === 'category';

  return (
    <div>
      <h2 className={classes.title}>{isCategory ? 'Add category' : 'Add competency'}</h2>
      <Formik
        initialValues={{ name: '', parentId: parent?.id ?? (null as string | null) }}
        validationSchema={Yup.object().shape({ name: Yup.string().trim().required('Required') })}
        onSubmit={async (values) => {
          setError(null);
          try {
            const created = await createCompetence({
              addToUser: 'false',
              competenceCreationParams: {
                name: values.name.trim(),
                root: isCategory,
                subCategory: isCategory && !!values.parentId,
                parentId: values.parentId,
                users: [],
              },
            }).unwrap();
            if (created?._id) onCreated?.(created._id, kind);
            onClose();
          } catch (e) {
            setError(errorMessage(e));
          }
        }}
      >
        {({ handleSubmit, values, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <Item title="Name" input={<Input placeholder="Name" name="name" type="text" />} />
            <Item
              title={isCategory ? 'Parent category' : 'Category'}
              input={
                <CategorySelect
                  tops={tops}
                  value={values.parentId}
                  onChange={(id) => setFieldValue('parentId', id)}
                  topLevelOnly={isCategory}
                  placeholder={isCategory ? 'None (top level)' : 'No category'}
                />
              }
            />
            {error && <p className={classes.error}>{error}</p>}
            <SettingsButtons discardCustom="Cancel" discard={onClose} saveCustom="Add" save={() => handleSubmit()} />
          </form>
        )}
      </Formik>
    </div>
  );
};

/** Rename, move (by changing the parent) or delete a category in one dialog,
 *  like a competency's Settings. */
export const EditCategoryDialog = ({
  category,
  tops,
  onClose,
  onDeleted,
}: {
  category: CategoryNode;
  tops: CategoryNode[];
  onClose: () => void;
  onDeleted?: () => void;
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [updateCompetence] = useUpdateCompetenceMutation();
  const [deleteCompetence] = useDeleteCompetenceMutation();
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const exclude = useMemo(() => subtreeCategoryIds(category), [category]);

  if (confirmingDelete) {
    const subcategories = category.children.length;
    const competencies = subtreeLeaves(category).length;
    if (subcategories > 0 || category.leaves.length > 0) {
      const parts = [
        subcategories > 0 && plural(subcategories, 'subcategory', 'subcategories'),
        competencies > 0 && plural(competencies, 'competency', 'competencies'),
      ].filter(Boolean);
      return (
        <div>
          <h2 className={classes.title}>{category.name} can't be deleted yet</h2>
          <p className={classes.text}>It still contains {parts.join(' and ')}. Move or delete them first.</p>
          <div className={classes.buttons}>
            <Button text="OK" onClick={() => setConfirmingDelete(false)} />
          </div>
        </div>
      );
    }
    return (
      <div>
        <h2 className={classes.title}>Are you sure you want to delete {category.name}?</h2>
        {error && <p className={classes.error}>{error}</p>}
        <div className={classes.buttons}>
          <Button text="Cancel" onClick={() => setConfirmingDelete(false)} />
          <Button
            text="Delete"
            background={theme.palette.red.opacity.r16}
            color={theme.palette.red.r4}
            onClick={async () => {
              setError(null);
              try {
                await deleteCompetence({ id: category.id }).unwrap();
                onDeleted?.();
                onClose();
              } catch (e) {
                setError(errorMessage(e));
              }
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className={classes.title}>Edit category</h2>
      <Formik
        initialValues={{ name: category.name, parentId: category.parent?.id ?? (null as string | null) }}
        validationSchema={Yup.object().shape({ name: Yup.string().trim().required('Required') })}
        onSubmit={async (values) => {
          setError(null);
          try {
            await updateCompetence({
              id: category.id,
              competenceUpdateParams: {
                name: values.name.trim(),
                root: true,
                subCategory: !!values.parentId,
                parentId: values.parentId,
              },
            }).unwrap();
            onClose();
          } catch (e) {
            setError(errorMessage(e));
          }
        }}
      >
        {({ handleSubmit, values, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <Item title="Name" input={<Input placeholder="Name" name="name" type="text" />} />
            {/* A top-level category with subcategories can't go under another one
                (that would make a third level), so there's nothing to choose. */}
            {!(category.children.length > 0 && !category.parent) && (
              <Item
                title="Parent category"
                input={
                  <CategorySelect
                    tops={tops}
                    value={values.parentId}
                    onChange={(id) => setFieldValue('parentId', id)}
                    exclude={exclude}
                    topLevelOnly
                    placeholder="None (top level)"
                  />
                }
              />
            )}
            <Item
              title="Delete category"
              alignRight
              input={
                <div className={classes.delete} onClick={() => setConfirmingDelete(true)}>
                  Delete
                </div>
              }
            />
            {error && <p className={classes.error}>{error}</p>}
            <SettingsButtons discardCustom="Cancel" discard={onClose} saveCustom="Save" save={() => handleSubmit()} />
          </form>
        )}
      </Formik>
    </div>
  );
};

/** The app's Modal (a bottom sheet on phones). */
export const DialogModal = ({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) => (
  <Modal visible={open} close={onClose}>
    {open ? children : null}
  </Modal>
);
